import React, { useEffect, useState } from 'react';
import { MapPin, Navigation, ArrowUpDown } from 'lucide-react';
import { SmartInput } from '@/components/ui/smart-input';
import { HapticButton } from '@/components/ui/haptic-button';
import { Card, CardContent } from '@/components/ui/card';
import { useLocationInputs } from '@/hooks/ride/useLocationInputs';
import type { RouteEndpoints } from '@/components/ride/routeEndpoints';
import { cn } from '@/lib/utils';

interface MobileLocationInputsProps {
  pickupLocation: string;
  destination: string;
  setPickupLocation: (location: string) => void;
  setDestination: (destination: string) => void;
  className?: string;
  /** See LocationInputs.onEndpointsChange — null while either field is mid-edit. */
  onEndpointsChange?: (endpoints: RouteEndpoints | null) => void;
}

export const MobileLocationInputs: React.FC<MobileLocationInputsProps> = ({
  pickupLocation,
  destination,
  setPickupLocation,
  setDestination,
  className,
  onEndpointsChange,
}) => {
  const [isSwapped, setIsSwapped] = useState(false);
  
  const {
    localPickup,
    localDestination,
    pickupSuggestions,
    destinationSuggestions,
    recentLocations,
    setLocalPickup,
    setLocalDestination,
    handleCurrentLocation,
    handleSelectPickupLocation,
    handleSelectDestination,
    commitPickup,
    commitDestination,
    routeEndpoints,
    isGettingLocation,
    isGeocodingLoading,
  } = useLocationInputs({
    initialPickup: pickupLocation,
    initialDestination: destination,
    onPickupChange: setPickupLocation,
    onDestinationChange: setDestination
  });

  useEffect(() => {
    onEndpointsChange?.(routeEndpoints);
  }, [routeEndpoints, onEndpointsChange]);

  const handleSwapLocations = () => {
    const tempPickup = localPickup;
    setLocalPickup(localDestination);
    setLocalDestination(tempPickup);
    setIsSwapped(!isSwapped);
  };

  const smartPickupSuggestions = [
    ...recentLocations.map(location => ({
      id: location,
      text: location,
      type: 'recent' as const,
      metadata: { description: 'Recent location' }
    })),
    ...pickupSuggestions.map(suggestion => ({
      id: suggestion.place_name,
      text: suggestion.place_name,
      type: 'location' as const,
      metadata: { description: suggestion.text }
    }))
  ];

  const smartDestinationSuggestions = [
    ...recentLocations.map(location => ({
      id: location,
      text: location,
      type: 'recent' as const,
      metadata: { description: 'Recent location' }
    })),
    ...destinationSuggestions.map(suggestion => ({
      id: suggestion.place_name,
      text: suggestion.place_name,
      type: 'location' as const,
      metadata: { description: suggestion.text }
    }))
  ];

  return (
    <Card className={cn("glass-morphism border-0", className)}>
      <CardContent className="p-4 space-y-4">
        <div className="relative">
          {/* Pickup Location */}
          <div className="relative z-10">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <div className="h-3 w-3 rounded-full bg-green-500" />
              </div>
              <div className="flex-1">
                <SmartInput
                  value={localPickup}
                  onChange={setLocalPickup}
                  placeholder="Pickup location"
                  suggestions={smartPickupSuggestions}
                  onSuggestionSelect={(suggestion) => handleSelectPickupLocation(suggestion.text)}
                  onBlur={() => commitPickup(localPickup)}
                  showVoiceButton={true}
                  enableVoiceToText={true}
                  contextualSuggestions={true}
                  className="border-0 bg-transparent focus-visible:ring-0 text-base touch-manipulation"
                />
              </div>
              <HapticButton
                variant="ghost"
                size="sm"
                onClick={handleCurrentLocation}
                disabled={isGettingLocation}
                hapticType="light"
                className="h-10 w-10 p-0 rounded-full"
              >
                <Navigation 
                  className={cn(
                    "h-4 w-4",
                    isGettingLocation && "animate-spin"
                  )} 
                />
              </HapticButton>
            </div>
          </div>

          {/* Swap Button */}
          <div className="absolute left-0 top-1/2 -translate-y-1/2 z-20 flex justify-center w-full pointer-events-none">
            <HapticButton
              variant="outline"
              size="sm"
              onClick={handleSwapLocations}
              hapticType="medium"
              className="pointer-events-auto h-8 w-8 p-0 rounded-full bg-background border-2 shadow-sm"
            >
              <ArrowUpDown className="h-3 w-3" />
            </HapticButton>
          </div>

          {/* Destination */}
          <div className="relative z-10 pt-6">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <div className="h-3 w-3 rounded-full bg-primary" />
              </div>
              <div className="flex-1">
                <SmartInput
                  value={localDestination}
                  onChange={setLocalDestination}
                  placeholder="Where to?"
                  suggestions={smartDestinationSuggestions}
                  onSuggestionSelect={(suggestion) => handleSelectDestination(suggestion.text)}
                  onBlur={() => commitDestination(localDestination)}
                  showVoiceButton={true}
                  enableVoiceToText={true}
                  contextualSuggestions={true}
                  className="border-0 bg-transparent focus-visible:ring-0 text-base touch-manipulation"
                />
              </div>
            </div>
          </div>

          {/* Connection Line */}
          <div className="absolute left-[5px] top-6 bottom-6 w-0.5 bg-border -z-10" />
        </div>
      </CardContent>
    </Card>
  );
};